export const TITLE_BLOCK_TYPES = {
  full_title: { fieldId: "generic_full_title", label: "Title" },
  full_subTitle: { fieldId: "generic_full_subtitle", label: "Subtitle" },
};

const TITLE_FIELD_IDS = new Set(
  Object.values(TITLE_BLOCK_TYPES).map((config) =>
    config.fieldId.toLowerCase(),
  ),
);

export const isTitleBlockField = (field) =>
  TITLE_FIELD_IDS.has(String(field?.field_id || "").toLowerCase());

const makeInstanceId = () =>
  typeof crypto !== "undefined" && crypto?.randomUUID
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

export const getTitleTypePatch = (type) => ({
  type: "PDFTextField",
  field_type: type,
  field_id: TITLE_BLOCK_TYPES[type].fieldId,
});

export const createTitleBlock = ({
  type,
  previewSection = null,
  sectionMeta = {},
}) => {
  const instanceId = makeInstanceId();

  return {
    name: `${TITLE_BLOCK_TYPES[type].fieldId}_${instanceId}`,
    field_instance_id: instanceId,
    ...getTitleTypePatch(type),
    is_generic: true,
    hidden: true,
    location: "create",
    field_label: "",
    label: "",
    value: "",
    preview_section: previewSection,
    // Same section metadata as the neighbouring widget, so getSections()
    // in the permit forms puts the title in the same card.
    ...(sectionMeta.block_id ? { block_id: sectionMeta.block_id } : {}),
    ...(sectionMeta.block_label
      ? { block_label: sectionMeta.block_label }
      : {}),
    ...(sectionMeta.section ? { section: sectionMeta.section } : {}),
    order: 0,
  };
};

export const getSectionMeta = (field) => ({
  block_id: field?.block_id,
  block_label: field?.block_label,
  section: field?.section,
});

export const mapTempFormFields = (previous, transform) => {
  const tempFields = previous?.document?.tempFields;
  if (!tempFields) return previous;

  return {
    ...previous,
    document: {
      ...previous.document,
      tempFields: {
        ...tempFields,
        formFields: transform(tempFields.formFields ?? []),
      },
    },
  };
};

// Inserts the title next to its anchor widgets, then re-indexes order
// exactly like the existing drag-and-drop reorder does.
export const insertTitleBlock = (
  rawFields = [],
  title,
  { placement = "end", anchorRawFields = [] } = {},
) => {
  const ordered = [...rawFields].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0),
  );
  let index = ordered.length;

  if (placement === "start") {
    index = 0;
  } else if (anchorRawFields.length) {
    const indexes = anchorRawFields
      .map((field) => ordered.indexOf(field))
      .filter((i) => i >= 0);

    if (indexes.length) {
      index =
        placement === "after" ? Math.max(...indexes) + 1 : Math.min(...indexes);
    }
  }

  ordered.splice(index, 0, title);
  return ordered.map((field, i) => ({ ...field, order: i }));
};

export const updateTitleBlock = (rawFields = [], target, patch) =>
  rawFields.map((field) =>
    isTitleBlockField(field) &&
    field.field_instance_id === target?.field_instance_id
      ? { ...field, ...patch }
      : field,
  );

export const removeTitleBlock = (rawFields = [], target) =>
  rawFields.filter(
    (field) =>
      !(
        isTitleBlockField(field) &&
        field.field_instance_id === target?.field_instance_id
      ),
  );

// Puts title blocks back into the section card they were created in.
// Falls back to the section holding the nearest following field, then to directItems.
export const mergeTitleBlocks = (
  { sections = [], directItems = [], allFields = [] },
  titleBlocks = [],
) => {
  if (!titleBlocks.length) return { sections, directItems, allFields };

  const nextSections = sections.map((section) => ({
    ...section,
    fields: [...section.fields],
  }));
  const nextDirectItems = [...directItems];
  const orderOf = (field) => Number(field?.order ?? field?.options?.[0]?.order);

  const findHome = (title) => {
    if (title.block_label) {
      const byBlockLabel = nextSections.find((section) =>
        section.fields.some((field) => field.block_label === title.block_label),
      );
      if (byBlockLabel) return byBlockLabel;
    }

    if (title.preview_section) {
      const exact = nextSections.find(
        (section) => section.section === title.preview_section,
      );
      if (exact) return exact;
    }

    if (!title.preview_section && !title.block_label) return null;

    const titleOrder = Number(title.order);
    if (!Number.isFinite(titleOrder)) return null;

    return (
      nextSections
        .map((section) => ({
          section,
          next: Math.min(
            ...section.fields.map(orderOf).filter((o) => o > titleOrder),
          ),
        }))
        .filter((entry) => Number.isFinite(entry.next))
        .sort((a, b) => a.next - b.next)[0]?.section ?? null
    );
  };

  titleBlocks.forEach((title) => {
    const home = findHome(title);
    (home ? home.fields : nextDirectItems).push(title);
  });

  return {
    sections: nextSections,
    directItems: nextDirectItems,
    allFields: [...allFields, ...titleBlocks],
  };
};
